// TanStack Query hooks for every public Supabase table.
// See vonlof_docs/07-DATA_MODEL.md for the schema.

import { useQuery } from '@tanstack/react-query';
import { supabase } from './supabase';
import { siteSettingsDefault } from './site-settings-default';
import type {
  AboutContent,
  AboutKey,
  Content,
  ContentCategory,
  FaqItem,
  FooterLink,
  HomeSection,
  Industry,
  Influencer,
  LegalPage,
  MediaAsset,
  Post,
  Project,
  Role,
  Service,
  ServicePricing,
  SiteSettings,
  TeamMember,
  Topic,
} from './cms-types';

const DEFAULT_STALE = 5 * 60 * 1000;

/* ------------------------------------------------------------------ site_settings */
export function useSiteSettings() {
  return useQuery<SiteSettings>({
    queryKey: ['site-settings'],
    staleTime: DEFAULT_STALE,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('data')
        .eq('id', 1)
        .maybeSingle();
      if (error) throw error;
      const merged: SiteSettings = { ...siteSettingsDefault, ...(data?.data ?? {}) };
      return merged;
    },
  });
}

/* ------------------------------------------------------------------ home_sections */
export function useHomeSections() {
  return useQuery<HomeSection[]>({
    queryKey: ['home-sections'],
    staleTime: DEFAULT_STALE,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('home_sections')
        .select('id, key, display_order, is_enabled, data')
        .eq('is_enabled', true)
        .order('display_order', { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as HomeSection[];
    },
  });
}

/** Fetch a single home section by key. */
export function useHomeSection<T = Record<string, unknown>>(key: string) {
  return useQuery<{ key: string; data: T } | null>({
    queryKey: ['home-section', key],
    staleTime: DEFAULT_STALE,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('home_sections')
        .select('key, data')
        .eq('key', key)
        .eq('is_enabled', true)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      return { key: data.key as string, data: (data.data ?? {}) as T };
    },
  });
}

/* ------------------------------------------------------------------ about_content */
export function useAboutContent<K extends AboutKey>(key: K) {
  return useQuery<Record<string, unknown> | null>({
    queryKey: ['about-content', key],
    staleTime: DEFAULT_STALE,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('about_content')
        .select('data')
        .eq('key', key)
        .maybeSingle();
      if (error) throw error;
      return (data?.data ?? null) as Record<string, unknown> | null;
    },
  });
}

export function useAllAboutContent() {
  return useQuery<AboutContent[]>({
    queryKey: ['about-content-all'],
    staleTime: DEFAULT_STALE,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('about_content')
        .select('id, key, data')
        .order('key');
      if (error) throw error;
      return (data ?? []) as unknown as AboutContent[];
    },
  });
}

/* ------------------------------------------------------------------ team_members */
export function useTeamMembers(department?: string) {
  return useQuery<TeamMember[]>({
    queryKey: ['team-members', department ?? 'all'],
    staleTime: DEFAULT_STALE,
    queryFn: async () => {
      let q = supabase
        .from('team_members')
        .select(
          'id, slug, name, role, departments, bio, bio_md, skills, portrait_media_id, linkedin_url, github_url, email, display_order, is_active',
        )
        .eq('status', 'published')
        .eq('is_active', true)
        .order('display_order', { ascending: true });
      // Array-column filter: row must include the requested department.
      if (department && department !== 'all') q = q.contains('departments', [department]);
      const { data, error } = await q;
      if (error) throw error;

      // Resolve portrait_media_id (UUID) → actual storage path in one round-trip,
      // then map back. Without this, publicAssetUrl would receive a media_id and
      // build a URL like /storage/v1/object/public/public/<uuid> which 404s.
      const rows = (data ?? []) as Array<Record<string, unknown>>;
      const pathByMediaId = await resolveMediaPaths(rows.map((r) => r.portrait_media_id as string | null));

      return rows.map((row) => {
        const portraitId = row.portrait_media_id as string | null;
        const portraitPath = portraitId ? pathByMediaId.get(portraitId) : null;
        return {
          id: row.id as string,
          slug: row.slug as string,
          name: row.name as string,
          role: row.role as string,
          departments: (row.departments as TeamMember['departments']) ?? [],
          bio: (row.bio as string | null) ?? null,
          bio_md: (row.bio_md as string | null) ?? null,
          skills: (row.skills as string[]) ?? [],
          portrait_url: portraitPath ? publicAssetUrl(portraitPath) : null,
          linkedin_url: (row.linkedin_url as string | null) ?? null,
          github_url: (row.github_url as string | null) ?? null,
          email: (row.email as string | null) ?? null,
          display_order: (row.display_order as number) ?? 0,
          is_active: (row.is_active as boolean) ?? true,
        } satisfies TeamMember;
      });
    },
  });
}

/* ------------------------------------------------------------------ projects */
export interface ProjectListFilters {
  category?: string;
  status?: 'shipped' | 'ongoing' | 'concept' | 'all';
  search?: string;
  sort?: 'newest' | 'oldest' | 'a-z' | 'featured';
}

export function useProjects(filters: ProjectListFilters = {}) {
  return useQuery<Project[]>({
    queryKey: ['projects', filters],
    staleTime: DEFAULT_STALE,
    queryFn: async () => {
      let q = supabase
        .from('projects')
        .select(
          'id, slug, title, summary, description_md, problem, approach, client, year, status, category, tech_stack, hero_media_id, business_impact, cta_label, cta_href, is_featured, display_order, published_at',
        )
        .eq('status_publish', 'published');
      if (filters.category && filters.category !== 'all') q = q.eq('category', filters.category);
      if (filters.status && filters.status !== 'all') q = q.eq('status', filters.status);
      if (filters.search && filters.search.trim()) {
        q = q.or(`title.ilike.%${filters.search}%,summary.ilike.%${filters.search}%`);
      }
      switch (filters.sort) {
        case 'oldest':
          q = q.order('published_at', { ascending: true });
          break;
        case 'a-z':
          q = q.order('title', { ascending: true });
          break;
        case 'featured':
          q = q.order('is_featured', { ascending: false }).order('published_at', { ascending: false });
          break;
        case 'newest':
        default:
          q = q.order('published_at', { ascending: false });
      }
      const { data, error } = await q;
      if (error) throw error;
      const rows = ((data ?? []) as Array<Record<string, unknown>>);
      const pathById = await resolveMediaPaths(rows.map((r) => r.hero_media_id as string | null));
      return rows.map((row) => {
        const id = row.hero_media_id as string | null;
        const path = id ? pathById.get(id) : undefined;
        return normaliseProject(row, path ?? null);
      });
    },
  });
}

export function useFeaturedProjects(limit = 3) {
  return useQuery<Project[]>({
    queryKey: ['projects-featured', limit],
    staleTime: DEFAULT_STALE,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select(
          'id, slug, title, summary, description_md, problem, approach, client, year, status, category, tech_stack, hero_media_id, business_impact, cta_label, cta_href, is_featured, display_order, published_at',
        )
        .eq('status_publish', 'published')
        .eq('is_featured', true)
        .order('display_order', { ascending: true })
        .limit(limit);
      if (error) throw error;
      const rows = ((data ?? []) as Array<Record<string, unknown>>);
      const pathById = await resolveMediaPaths(rows.map((r) => r.hero_media_id as string | null));
      return rows.map((row) => {
        const id = row.hero_media_id as string | null;
        const path = id ? pathById.get(id) : undefined;
        return normaliseProject(row, path ?? null);
      });
    },
  });
}

export function useProjectBySlug(slug: string | undefined) {
  return useQuery<Project | null>({
    queryKey: ['project', slug],
    enabled: !!slug,
    staleTime: DEFAULT_STALE,
    queryFn: async () => {
      if (!slug) return null;
      const { data, error } = await supabase
        .from('projects')
        .select(
          'id, slug, title, summary, description_md, problem, approach, client, year, status, category, tech_stack, hero_media_id, business_impact, cta_label, cta_href, is_featured, display_order, published_at',
        )
        .eq('slug', slug)
        .eq('status_publish', 'published')
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const project = normaliseProject(data);

      // Fetch features + images in parallel.
      const [featuresRes, imagesRes] = await Promise.all([
        supabase
          .from('project_features')
          .select('id, icon, title, description, display_order')
          .eq('project_id', project.id)
          .order('display_order'),
        supabase
          .from('project_images')
          .select('id, media_id, alt, display_order')
          .eq('project_id', project.id)
          .order('display_order'),
      ]);
      const features =
        (featuresRes.data ?? []).map((f) => ({
          id: f.id as string,
          icon: (f.icon as string | null) ?? null,
          title: f.title as string,
          description: (f.description as string | null) ?? null,
          display_order: (f.display_order as number) ?? 0,
        })) ?? [];

      // Resolve every media_id (hero + gallery) in one round-trip so the
      // gallery URLs and the hero URL both point at real storage paths.
      const heroMediaIdRaw = (data as { hero_media_id?: string | null }).hero_media_id ?? null;
      const pathById = await resolveMediaPaths([
        heroMediaIdRaw,
        ...(imagesRes.data ?? []).map((img) => img.media_id as string | null),
      ]);
      const heroPath = heroMediaIdRaw ? pathById.get(heroMediaIdRaw) ?? null : null;
      const projectWithHero = {
        ...project,
        hero_image_url: heroPath ? publicAssetUrl(heroPath) : null,
      };
      const gallery =
        (imagesRes.data ?? []).map((img) => {
          const mediaId = img.media_id as string | null;
          const path = mediaId ? pathById.get(mediaId) : null;
          return {
            id: img.id as string,
            url: path ? publicAssetUrl(path) : '',
            alt: img.alt as string,
            display_order: (img.display_order as number) ?? 0,
          };
        }) ?? [];
      return { ...projectWithHero, features, gallery };
    },
  });
}

function normaliseProject(row: Record<string, unknown>, heroPath?: string | null | undefined): Project {
  const heroMediaId = (row.hero_media_id as string | null) ?? null;
  // heroPath is a storage path (e.g. "1786...-yeemor.jpg") resolved from
  // media_assets — it must be wrapped in publicAssetUrl() before use. Falling
  // back to passing the media_id through publicAssetUrl keeps list views
  // working even if the join failed.
  const heroUrl = heroPath
    ? publicAssetUrl(heroPath)
    : heroMediaId
      ? publicAssetUrl(heroMediaId)
      : null;
  return {
    id: row.id as string,
    slug: row.slug as string,
    title: row.title as string,
    summary: (row.summary as string | null) ?? null,
    description_md: (row.description_md as string | null) ?? null,
    problem: (row.problem as string | null) ?? null,
    approach: (row.approach as string | null) ?? null,
    client: (row.client as string | null) ?? null,
    year: (row.year as number | null) ?? null,
    status: row.status as Project['status'],
    category: row.category as Project['category'],
    tech_stack: (row.tech_stack as string[]) ?? [],
    hero_image_url: heroUrl,
    gallery: [],
    features: [],
    business_impact:
      (row.business_impact as Array<{ value: string; label: string; sublabel?: string }>) ?? [],
    cta_label: (row.cta_label as string | null) ?? null,
    cta_href: (row.cta_href as string | null) ?? null,
    is_featured: (row.is_featured as boolean) ?? false,
    display_order: (row.display_order as number) ?? 0,
    published_at: (row.published_at as string | null) ?? null,
  };
}

/* ------------------------------------------------------------------ posts */
export function usePosts(limit?: number, category?: string) {
  return useQuery<Post[]>({
    queryKey: ['posts', { limit, category }],
    staleTime: DEFAULT_STALE,
    queryFn: async () => {
      let q = supabase
        .from('posts')
        .select(
          'id, slug, title, excerpt, body_markdown, hero_media_id, author_id, category, tags, reading_time_min, status, published_at, created_at, updated_at',
        )
        .eq('status', 'published')
        .order('published_at', { ascending: false });
      if (category && category !== 'all') q = q.eq('category', category);
      if (limit) q = q.limit(limit);
      const { data, error } = await q;
      if (error) throw error;
      const rows = (data ?? []) as Array<Record<string, unknown>>;
      const pathById = await resolveMediaPaths(rows.map((r) => r.hero_media_id as string | null));
      return rows.map((row) => {
        const id = row.hero_media_id as string | null;
        const path = id ? pathById.get(id) : undefined;
        return normalisePost(row, path ?? null);
      });
    },
  });
}

export function usePostBySlug(slug: string | undefined) {
  return useQuery<Post | null>({
    queryKey: ['post', slug],
    enabled: !!slug,
    staleTime: DEFAULT_STALE,
    queryFn: async () => {
      if (!slug) return null;
      const { data, error } = await supabase
        .from('posts')
        .select(
          'id, slug, title, excerpt, body_markdown, hero_media_id, author_id, category, tags, reading_time_min, status, published_at, created_at, updated_at',
        )
        .eq('slug', slug)
        .eq('status', 'published')
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      // Resolve hero + author portrait paths together so both URLs work.
      const authorId = (data.author_id as string | null) ?? null;
      const heroMediaId = (data.hero_media_id as string | null) ?? null;
      const { data: author } = authorId
        ? await supabase
            .from('team_members')
            .select('id, slug, name, role, portrait_media_id')
            .eq('id', authorId)
            .maybeSingle()
        : { data: null };
      const pathById = await resolveMediaPaths([
        heroMediaId,
        author?.portrait_media_id ?? null,
      ]);
      const heroPath = heroMediaId ? pathById.get(heroMediaId) ?? null : null;
      const post = normalisePost(data, heroPath);
      if (author) {
        const portraitMediaId = (author.portrait_media_id as string | null) ?? null;
        const portraitPath = portraitMediaId ? pathById.get(portraitMediaId) ?? null : null;
        post.author = {
          id: author.id as string,
          slug: author.slug as string,
          name: author.name as string,
          role: author.role as string,
          portrait_url: portraitPath ? publicAssetUrl(portraitPath) : null,
        };
      }
      return post;
    },
  });
}

export function useRelatedPosts(postId: string | undefined, category: string | undefined) {
  return useQuery<Post[]>({
    queryKey: ['related-posts', postId, category],
    enabled: !!postId && !!category,
    staleTime: DEFAULT_STALE,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('posts')
        .select(
          'id, slug, title, excerpt, body_markdown, hero_media_id, author_id, category, tags, reading_time_min, status, published_at, created_at, updated_at',
        )
        .eq('status', 'published')
        .eq('category', category ?? '')
        .neq('id', postId ?? '')
        .order('published_at', { ascending: false })
        .limit(3);
      if (error) throw error;
      const rows = (data ?? []) as Array<Record<string, unknown>>;
      const pathById = await resolveMediaPaths(rows.map((r) => r.hero_media_id as string | null));
      return rows.map((row) => {
        const id = row.hero_media_id as string | null;
        const path = id ? pathById.get(id) : undefined;
        return normalisePost(row, path ?? null);
      });
    },
  });
}

function normalisePost(row: Record<string, unknown>, heroPath?: string | null | undefined): Post {
  const heroMediaId = (row.hero_media_id as string | null) ?? null;
  // heroPath is a storage path resolved from media_assets — wrap it. Fallback
  // to publicAssetUrl(media_id) only if the join failed.
  const heroUrl = heroPath
    ? publicAssetUrl(heroPath)
    : heroMediaId
      ? publicAssetUrl(heroMediaId)
      : null;
  return {
    id: row.id as string,
    slug: row.slug as string,
    title: row.title as string,
    excerpt: (row.excerpt as string | null) ?? null,
    body_markdown: (row.body_markdown as string | null) ?? '',
    hero_image_url: heroUrl,
    author_id: (row.author_id as string | null) ?? null,
    author: null,
    category: (row.category as string | null) ?? 'Uncategorised',
    tags: (row.tags as string[]) ?? [],
    reading_time_min: (row.reading_time_min as number | null) ?? null,
    status: row.status as Post['status'],
    published_at: (row.published_at as string | null) ?? null,
    created_at: (row.created_at as string | null) ?? new Date().toISOString(),
    updated_at: (row.updated_at as string | null) ?? new Date().toISOString(),
    meta_title: (row.meta_title as string | null) ?? null,
    meta_description: (row.meta_description as string | null) ?? null,
  };
}

/* ------------------------------------------------------------------ services */
export function useServices() {
  return useQuery<Service[]>({
    queryKey: ['services'],
    staleTime: DEFAULT_STALE,
    queryFn: async () => {
      const [servicesRes, topicsRes] = await Promise.all([
        supabase
          .from('services')
          .select('id, slug, title, summary, description_md, icon, deliverables, tech_strip, cta_label, cta_href, display_order, is_featured, meta_title, meta_description, topic_id, pricing')
          .eq('status_publish', 'published')
          .order('display_order', { ascending: true }),
        supabase.from('topics').select('id, slug').eq('is_active', true),
      ]);
      if (servicesRes.error) throw servicesRes.error;
      if (topicsRes.error) throw topicsRes.error;
      const topicById: Record<string, string> = {};
      for (const t of topicsRes.data ?? []) {
        topicById[t.id] = t.slug;
      }
      return ((servicesRes.data ?? []) as Array<Record<string, unknown>>).map((r) => ({
        id: r.id as string,
        slug: r.slug as string,
        title: r.title as string,
        summary: (r.summary as string | null) ?? null,
        description_md: (r.description_md as string | null) ?? null,
        icon: (r.icon as string | null) ?? null,
        deliverables: (r.deliverables as string[]) ?? [],
        tech_strip: (r.tech_strip as string[]) ?? [],
        cta_label: (r.cta_label as string | null) ?? 'Starting from',
        cta_href: (r.cta_href as string | null) ?? '/contact',
        display_order: (r.display_order as number) ?? 0,
        is_featured: (r.is_featured as boolean) ?? false,
        meta_title: (r.meta_title as string | null) ?? null,
        meta_description: (r.meta_description as string | null) ?? null,
        topic_slug: (r.topic_id as string | null) ? (topicById[r.topic_id as string] ?? null) : null,
        pricing: (r.pricing as ServicePricing | null) ?? null,
      }));
    },
  });
}

export function useFeaturedServices() {
  return useQuery<Service[]>({
    queryKey: ['services-featured'],
    staleTime: DEFAULT_STALE,
    queryFn: async () => {
      const [servicesRes, topicsRes] = await Promise.all([
        supabase
          .from('services')
          .select('id, slug, title, summary, description_md, icon, deliverables, tech_strip, cta_label, cta_href, display_order, is_featured, meta_title, meta_description, topic_id, pricing')
          .eq('status_publish', 'published')
          .eq('is_featured', true)
          .order('display_order', { ascending: true })
          .limit(6),
        supabase.from('topics').select('id, slug').eq('is_active', true),
      ]);
      if (servicesRes.error) throw servicesRes.error;
      if (topicsRes.error) throw topicsRes.error;
      const topicById: Record<string, string> = {};
      for (const t of topicsRes.data ?? []) {
        topicById[t.id] = t.slug;
      }
      return ((servicesRes.data ?? []) as Array<Record<string, unknown>>).map((r) => ({
        id: r.id as string,
        slug: r.slug as string,
        title: r.title as string,
        summary: (r.summary as string | null) ?? null,
        description_md: (r.description_md as string | null) ?? null,
        icon: (r.icon as string | null) ?? null,
        deliverables: (r.deliverables as string[]) ?? [],
        tech_strip: (r.tech_strip as string[]) ?? [],
        cta_label: (r.cta_label as string | null) ?? 'Starting from',
        cta_href: (r.cta_href as string | null) ?? '/contact',
        display_order: (r.display_order as number) ?? 0,
        is_featured: (r.is_featured as boolean) ?? false,
        meta_title: (r.meta_title as string | null) ?? null,
        meta_description: (r.meta_description as string | null) ?? null,
        topic_slug: (r.topic_id as string | null) ? (topicById[r.topic_id as string] ?? null) : null,
        pricing: (r.pricing as ServicePricing | null) ?? null,
      }));
    },
  });
}

/* ------------------------------------------------------------------ topics */
export function useTopics() {
  return useQuery<Topic[]>({
    queryKey: ['topics'],
    staleTime: DEFAULT_STALE,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('topics')
        .select('id, name, slug, display_order, is_active')
        .eq('is_active', true)
        .order('display_order', { ascending: true });
      if (error) throw error;
      return ((data ?? []) as Array<Record<string, unknown>>).map((r) => ({
        id: r.id as string,
        name: r.name as string,
        slug: r.slug as string,
        display_order: r.display_order as number,
        is_active: r.is_active as boolean,
      }));
    },
  });
}

/* ------------------------------------------------------------------ industries */
export function useIndustries() {
  return useQuery<Industry[]>({
    queryKey: ['industries'],
    staleTime: DEFAULT_STALE,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('industries')
        .select('id, slug, name, icon, outcome, display_order')
        .eq('is_enabled', true)
        .order('display_order', { ascending: true });
      if (error) throw error;
      return ((data ?? []) as Array<Record<string, unknown>>).map((r) => ({
        id: r.id as string,
        slug: r.slug as string,
        name: r.name as string,
        icon: (r.icon as string | null) ?? null,
        outcome: (r.outcome as string | null) ?? null,
        display_order: (r.display_order as number) ?? 0,
      }));
    },
  });
}

/* ------------------------------------------------------------------ roles */
export function useOpenRoles() {
  return useQuery<Role[]>({
    queryKey: ['roles-open'],
    staleTime: DEFAULT_STALE,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('roles')
        .select(
          'id, slug, title, department, location, type, description_md, responsibilities, requirements, nice_to_haves, is_open, apply_email, published_at',
        )
        .eq('status_publish', 'published')
        .eq('is_open', true)
        .order('published_at', { ascending: false });
      if (error) throw error;
      return ((data ?? []) as Array<Record<string, unknown>>).map((r) => ({
        id: r.id as string,
        slug: r.slug as string,
        title: r.title as string,
        department: (r.department as string | null) ?? null,
        location: (r.location as string | null) ?? null,
        type: (r.type as Role['type'] | null) ?? null,
        description_md: (r.description_md as string | null) ?? null,
        responsibilities: (r.responsibilities as string[]) ?? [],
        requirements: (r.requirements as string[]) ?? [],
        nice_to_haves: (r.nice_to_haves as string[]) ?? [],
        is_open: (r.is_open as boolean) ?? true,
        apply_email: (r.apply_email as string | null) ?? null,
        published_at: (r.published_at as string | null) ?? null,
      }));
    },
  });
}

export function useRoleBySlug(slug: string | undefined) {
  return useQuery<Role | null>({
    queryKey: ['role', slug],
    enabled: !!slug,
    staleTime: DEFAULT_STALE,
    queryFn: async () => {
      if (!slug) return null;
      const { data, error } = await supabase
        .from('roles')
        .select(
          'id, slug, title, department, location, type, description_md, responsibilities, requirements, nice_to_haves, is_open, apply_email, published_at',
        )
        .eq('slug', slug)
        .eq('status_publish', 'published')
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      return {
        id: data.id as string,
        slug: data.slug as string,
        title: data.title as string,
        department: (data.department as string | null) ?? null,
        location: (data.location as string | null) ?? null,
        type: (data.type as Role['type'] | null) ?? null,
        description_md: (data.description_md as string | null) ?? null,
        responsibilities: (data.responsibilities as string[]) ?? [],
        requirements: (data.requirements as string[]) ?? [],
        nice_to_haves: (data.nice_to_haves as string[]) ?? [],
        is_open: (data.is_open as boolean) ?? true,
        apply_email: (data.apply_email as string | null) ?? null,
        published_at: (data.published_at as string | null) ?? null,
      };
    },
  });
}

/* ------------------------------------------------------------------ faq */
export function useFaq(category?: string) {
  return useQuery<FaqItem[]>({
    queryKey: ['faq', category ?? 'all'],
    staleTime: DEFAULT_STALE,
    queryFn: async () => {
      let q = supabase
        .from('faq')
        .select('id, question, answer_md, category, display_order, is_published')
        .eq('is_published', true)
        .order('display_order', { ascending: true });
      if (category && category !== 'all') q = q.eq('category', category);
      const { data, error } = await q;
      if (error) throw error;
      return ((data ?? []) as Array<Record<string, unknown>>).map((r) => ({
        id: r.id as string,
        question: r.question as string,
        answer_md: r.answer_md as string,
        category: (r.category as string | null) ?? 'general',
        display_order: (r.display_order as number) ?? 0,
        is_published: (r.is_published as boolean) ?? true,
      }));
    },
  });
}

/* ------------------------------------------------------------------ legal */
export function useLegalPage(slug: 'privacy' | 'terms' | 'cookies' | undefined) {
  return useQuery<LegalPage | null>({
    queryKey: ['legal', slug],
    enabled: !!slug,
    staleTime: DEFAULT_STALE,
    queryFn: async () => {
      if (!slug) return null;
      const { data, error } = await supabase
        .from('legal_pages')
        .select('id, slug, title, body_md, effective_at, updated_at')
        .eq('slug', slug)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      return {
        id: data.id as string,
        slug: data.slug as LegalPage['slug'],
        title: data.title as string,
        body_md: data.body_md as string,
        effective_at: (data.effective_at as string | null) ?? null,
        updated_at: (data.updated_at as string | null) ?? '',
      };
    },
  });
}

/* ------------------------------------------------------------------ footer_links */
export function useFooterLinks() {
  return useQuery<FooterLink[]>({
    queryKey: ['footer-links'],
    staleTime: DEFAULT_STALE,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('footer_links')
        .select('id, column, display_order, label, href, is_external')
        .order('column')
        .order('display_order', { ascending: true });
      if (error) throw error;
      return ((data ?? []) as Array<Record<string, unknown>>).map((r) => ({
        id: r.id as string,
        column: r.column as FooterLink['column'],
        display_order: (r.display_order as number) ?? 0,
        label: r.label as string,
        href: r.href as string,
        is_external: (r.is_external as boolean) ?? false,
      }));
    },
  });
}

/* ------------------------------------------------------------------ media */
export function publicAssetUrl(mediaId: string): string {
  if (!mediaId) return '';
  // Cloudflare Images will be mounted in front; for now, fall back to the
  // Supabase public storage URL.  The shape matches what the admin uploader
  // writes into media_assets.path.
  const base = (import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? '';
  return `${base}/storage/v1/object/public/public/${mediaId}`;
}

/**
 * Resolve a list of media_assets.id UUIDs to their storage `path`s in one
 * round-trip. Caller passes raw row columns like `portrait_media_id` /
 * `hero_media_id`; gets back a Map<id, path> for joining back into the rows.
 */
async function resolveMediaPaths(ids: Array<string | null>): Promise<Map<string, string>> {
  const uniq = Array.from(new Set(ids.filter((x): x is string => !!x)));
  const out = new Map<string, string>();
  if (uniq.length === 0) return out;
  const { data, error } = await supabase
    .from('media_assets')
    .select('id, path')
    .in('id', uniq);
  if (error) throw error;
  for (const m of data ?? []) out.set(m.id, m.path);
  return out;
}

export function useMediaAssets(tags?: string[]) {
  return useQuery<MediaAsset[]>({
    queryKey: ['media-assets', tags ?? 'all'],
    staleTime: DEFAULT_STALE,
    queryFn: async () => {
      let q = supabase
        .from('media_assets')
        .select('id, bucket, path, filename, alt, width, height, bytes, mime, tags')
        .eq('bucket', 'public')
        .order('created_at', { ascending: false });
      if (tags && tags.length > 0) q = q.overlaps('tags', tags);
      const { data, error } = await q;
      if (error) throw error;
      return ((data ?? []) as Array<Record<string, unknown>>).map((r) => ({
        id: r.id as string,
        bucket: r.bucket as MediaAsset['bucket'],
        path: r.path as string,
        url: `${(import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? ''}/storage/v1/object/public/${r.bucket as string}/${r.path as string}`,
        filename: (r.filename as string | null) ?? '',
        alt: (r.alt as string | null) ?? '',
        width: (r.width as number | null) ?? null,
        height: (r.height as number | null) ?? null,
        bytes: (r.bytes as number | null) ?? null,
        mime: (r.mime as string | null) ?? 'application/octet-stream',
        tags: (r.tags as string[]) ?? [],
      }));
    },
  });
}

/* ------------------------------------------------------------------ influencers */
export function usePublishedInfluencers() {
  return useQuery<Influencer[]>({
    queryKey: ['influencers'],
    staleTime: DEFAULT_STALE,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('influencers')
        .select(
          'id, slug, name, headline, bio_md, portrait_media_id, tiktok_url, instagram_url, youtube_url, facebook_url, joined_at, is_active',
        )
        .eq('status', 'published')
        .eq('is_active', true)
        .order('joined_at', { ascending: false });
      if (error) throw error;
      const rows = (data ?? []) as Array<Record<string, unknown>>;
      const pathById = await resolveMediaPaths(rows.map((r) => r.portrait_media_id as string | null));
      return rows.map((row) =>
        normaliseInfluencer(row, pathById.get(row.portrait_media_id as string) ?? null),
      );
    },
  });
}

/* ------------------------------------------------------------------ contents */
export function usePublishedContents(category?: ContentCategory | 'all') {
  return useQuery<Content[]>({
    queryKey: ['contents', category ?? 'all'],
    staleTime: DEFAULT_STALE,
    queryFn: async () => {
      let q = supabase
        .from('contents')
        .select(
          'id, slug, title, summary, description_md, cover_media_id, tiktok_url, instagram_url, youtube_url, x_url, category, is_featured, published_at, primary_influencer_id',
        )
        .eq('status', 'published')
        .order('is_featured', { ascending: false })
        .order('display_order', { ascending: true })
        .order('published_at', { ascending: false });
      if (category && category !== 'all') q = q.eq('category', category);
      const { data, error } = await q;
      if (error) throw error;
      const rows = (data ?? []) as Array<Record<string, unknown>>;
      const pathById = await resolveMediaPaths(rows.map((r) => r.cover_media_id as string | null));
      const influencerIds = rows
        .map((r) => r.primary_influencer_id as string | null)
        .filter((x): x is string => !!x);
      const influencerMap = await resolveInfluencersById(influencerIds);
      return rows.map((row) => {
        const mediaId = row.cover_media_id as string | null;
        const path = mediaId ? pathById.get(mediaId) ?? null : null;
        const infId = row.primary_influencer_id as string | null;
        return normaliseContent(row, path, infId ? influencerMap.get(infId) ?? null : null);
      });
    },
  });
}

export function useContentBySlug(slug: string | undefined) {
  return useQuery<Content | null>({
    queryKey: ['content', slug],
    enabled: !!slug,
    staleTime: DEFAULT_STALE,
    queryFn: async () => {
      if (!slug) return null;
      const { data, error } = await supabase
        .from('contents')
        .select(
          'id, slug, title, summary, description_md, cover_media_id, tiktok_url, instagram_url, youtube_url, x_url, category, is_featured, published_at, primary_influencer_id',
        )
        .eq('slug', slug)
        .eq('status', 'published')
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const row = data as Record<string, unknown>;
      const [pathById, influencerMap] = await Promise.all([
        resolveMediaPaths([row.cover_media_id as string | null]),
        resolveInfluencersById(
          (row.primary_influencer_id as string | null) ? [row.primary_influencer_id as string] : [],
        ),
      ]);
      const mediaId = row.cover_media_id as string | null;
      const path = mediaId ? pathById.get(mediaId) ?? null : null;
      const infId = row.primary_influencer_id as string | null;
      return normaliseContent(row, path, infId ? influencerMap.get(infId) ?? null : null);
    },
  });
}

function normaliseInfluencer(
  row: Record<string, unknown>,
  portraitPath: string | null | undefined,
): Influencer {
  return {
    id: row.id as string,
    slug: row.slug as string,
    name: row.name as string,
    headline: (row.headline as string | null) ?? null,
    bio_md: (row.bio_md as string | null) ?? null,
    portrait_url: portraitPath ? publicAssetUrl(portraitPath) : null,
    tiktok_url: (row.tiktok_url as string | null) ?? null,
    instagram_url: (row.instagram_url as string | null) ?? null,
    youtube_url: (row.youtube_url as string | null) ?? null,
    facebook_url: (row.facebook_url as string | null) ?? null,
    joined_at: (row.joined_at as string | null) ?? null,
    is_active: (row.is_active as boolean) ?? true,
  };
}

function normaliseContent(
  row: Record<string, unknown>,
  coverPath: string | null | undefined,
  influencer: Influencer | null,
): Content {
  const coverMediaId = (row.cover_media_id as string | null) ?? null;
  const coverUrl = coverPath
    ? publicAssetUrl(coverPath)
    : coverMediaId
      ? publicAssetUrl(coverMediaId)
      : null;
  return {
    id: row.id as string,
    slug: row.slug as string,
    title: row.title as string,
    summary: row.summary as string,
    description_md: (row.description_md as string | null) ?? null,
    cover_url: coverUrl,
    primary_influencer: influencer,
    tiktok_url: (row.tiktok_url as string | null) ?? null,
    instagram_url: (row.instagram_url as string | null) ?? null,
    youtube_url: (row.youtube_url as string | null) ?? null,
    x_url: (row.x_url as string | null) ?? null,
    category: row.category as Content['category'],
    is_featured: (row.is_featured as boolean) ?? false,
    published_at: (row.published_at as string | null) ?? null,
  };
}

async function resolveInfluencersById(ids: Array<string>): Promise<Map<string, Influencer>> {
  const uniq = Array.from(new Set(ids.filter((x): x is string => !!x)));
  const out = new Map<string, Influencer>();
  if (uniq.length === 0) return out;
  const { data, error } = await supabase
    .from('influencers')
    .select(
      'id, slug, name, headline, bio_md, portrait_media_id, tiktok_url, instagram_url, youtube_url, facebook_url, joined_at, is_active',
    )
    .in('id', uniq);
  if (error) throw error;
  const pathByMediaId = await resolveMediaPaths(
    (data ?? []).map((r) => r.portrait_media_id as string | null),
  );
  for (const r of data ?? []) {
    const portraitId = r.portrait_media_id as string | null;
    const portraitPath = portraitId ? pathByMediaId.get(portraitId) ?? null : null;
    out.set(r.id as string, normaliseInfluencer(r, portraitPath));
  }
  return out;
}

export interface ProjectCategory {
  id: string;
  name: string;
  slug: string;
  display_order: number;
}

export function useProjectCategories() {
  return useQuery<ProjectCategory[]>({
    queryKey: ['project-categories'],
    staleTime: DEFAULT_STALE,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('project_categories')
        .select('*')
        .order('display_order');
      if (error) throw error;
      return (data ?? []) as ProjectCategory[];
    },
  });
}